import React from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

const ExpenseBreakdownModal = ({ expense, isOpen, onClose }) => {
  const { t } = useTranslation();

  if (!isOpen || !expense) return null;

  const formatCurrency = (amount) => {
    return `SAR ${(amount || 0).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  const formatDateTime = (expense) => {
    let expenseDate;
    if (expense.date) {
      expenseDate = expense.date?.toDate ? expense.date.toDate() : new Date(expense.date);
    } else if (expense.expenseDate) {
      expenseDate = expense.expenseDate?.toDate ? expense.expenseDate.toDate() : new Date(expense.expenseDate);
    } else if (expense.createdAt) {
      expenseDate = expense.createdAt?.toDate ? expense.createdAt.toDate() : new Date(expense.createdAt);
    } else {
      expenseDate = new Date();
    }

    if (isNaN(expenseDate.getTime())) {
      expenseDate = new Date();
    }

    return expenseDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 max-w-2xl shadow-lg rounded-lg bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {t('accounting.expense_breakdown', 'Expense Breakdown')}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t('accounting.detailed_vat_breakdown', 'Detailed VAT breakdown and expense information')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Expense Information */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <InformationCircleIcon className="w-5 h-5 mr-2 text-blue-600" />
              {t('accounting.expense_information', 'Expense Information')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('common.date', 'Date')}
                </label>
                <p className="text-sm text-gray-900">{formatDateTime(expense)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('accounting.category', 'Category')}
                </label>
                <p className="text-sm text-gray-900">{expense.category || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('accounting.vendor', 'Vendor')}
                </label>
                <p className="text-sm text-gray-900">{expense.vendor || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  {t('common.status', 'Status')}
                </label>
                <p className="text-sm text-gray-900">{expense.status || 'pending'}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('common.description', 'Description')}
                </label>
                <p className="text-sm text-gray-900">{expense.description || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* VAT Breakdown */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
              <span className="w-2 h-6 bg-blue-500 rounded mr-3"></span>
              {t('accounting.vat_breakdown', 'VAT Breakdown')}
            </h4>
            
            <div className="space-y-4">
              {/* Base Amount */}
              <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    {t('accounting.base_amount', 'Base Amount')}
                  </span>
                  <p className="text-xs text-gray-500">
                    {t('accounting.amount_before_vat', 'Amount before VAT')}
                  </p>
                </div>
                <span className="text-lg font-bold text-gray-900">
                  {formatCurrency(expense.baseAmount || expense.amount || 0)}
                </span>
              </div>

              {/* VAT Amount */}
              <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    {t('accounting.vat_amount', 'VAT Amount')} (15%)
                  </span>
                  <p className="text-xs text-gray-500">
                    {t('accounting.saudi_vat_rate', 'Saudi VAT rate applied')}
                  </p>
                </div>
                <span className="text-lg font-bold text-orange-600">
                  {formatCurrency(expense.vatAmount || 0)}
                </span>
              </div>

              {/* Total Amount */}
              <div className="flex justify-between items-center p-3 bg-blue-100 rounded-lg border-2 border-blue-300">
                <div>
                  <span className="text-sm font-medium text-blue-900">
                    {t('accounting.total_amount', 'Total Amount')}
                  </span>
                  <p className="text-xs text-blue-700">
                    {t('accounting.final_amount_paid', 'Final amount paid')}
                  </p>
                </div>
                <span className="text-xl font-bold text-blue-900">
                  {formatCurrency(expense.totalAmount || expense.amount || 0)}
                </span>
              </div>

              {/* VAT Inclusion Status */}
              {expense.vatIncluded !== undefined && (
                <div className="flex justify-between items-center p-3 bg-gray-100 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">
                    {t('accounting.vat_calculation_method', 'VAT Calculation Method')}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    expense.vatIncluded 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {expense.vatIncluded 
                      ? t('accounting.vat_inclusive', 'VAT Inclusive') 
                      : t('accounting.vat_exclusive', 'VAT Exclusive')
                    }
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Information */}
          {(expense.paymentMethod || expense.reference || expense.notes) && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                {t('accounting.additional_information', 'Additional Information')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {expense.paymentMethod && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t('accounting.payment_method', 'Payment Method')}
                    </label>
                    <p className="text-sm text-gray-900">{expense.paymentMethod}</p>
                  </div>
                )}
                {expense.reference && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {t('accounting.reference', 'Reference')}
                    </label>
                    <p className="text-sm text-gray-900">{expense.reference}</p>
                  </div>
                )}
                {expense.notes && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('accounting.notes', 'Notes')}
                    </label>
                    <p className="text-sm text-gray-900">{expense.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Shipment Link */}
          {expense.relatedShipmentId && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-yellow-800 mb-2">
                {t('accounting.related_shipment', 'Related Shipment')}
              </h4>
              <a
                href={`/shipments/${expense.relatedShipmentId}`}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = `/shipments/${expense.relatedShipmentId}`;
                }}
              >
                {t('accounting.view_shipment_details', 'View Shipment Details')}
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            {t('common.close', 'Close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseBreakdownModal;